/**
 * HelpPopup Component
 *
 * A modal popup that displays contextual help content for a feature.
 * Includes "הבנתי" (Got it) button and "שאל שאלה" (Ask a question) flow
 * that creates a support ticket of category "help_question".
 */

import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import {
  Question,
  PaperPlaneTilt,
  CheckCircle,
  ChatText,
  CaretDown,
} from "@phosphor-icons/react";
import LoaderLoop from "./loader/LoaderLoop";
import {
  selectTherapistProfile,
  selectClinicProfile,
  selectIsAdmin,
} from "../../store/slices/authSlice";
import { HELP_FAQ } from "../../config/helpFaq";
import { useCreateTicketMutation } from "../../store/services/supportApi";
import { useToast } from "../../contexts/ToastContext";
import ModalCloseButton from "../ui/ModalCloseButton";
import "./HelpPopup.css";

function HelpPopup({ featureKey, helpData, onClose }) {
  const [showAskForm, setShowAskForm] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [ticketSent, setTicketSent] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = useCallback((index) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  }, []);

  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicProfile = useSelector(selectClinicProfile);
  const isAdmin = useSelector(selectIsAdmin);

  // Filter FAQ items by audience — solo clinics don't need
  // multi-therapist questions, therapists don't need admin-only ones.
  const isSoloClinic = clinicProfile?.isSoloClinic === true;
  const faqItems = (HELP_FAQ[featureKey] || []).filter((item) => {
    switch (item.showIf) {
      case "multiTherapist":
        return !isSoloClinic;
      case "soloClinic":
        return isSoloClinic;
      case "admin":
        return isAdmin;
      case "therapist":
        return !isAdmin;
      default:
        return true;
    }
  });
  const { showSuccess, showError } = useToast();
  const [createTicket, { isLoading: isSending }] = useCreateTicketMutation();

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleSendQuestion = useCallback(async () => {
    if (!questionText.trim()) return;

    try {
      const therapistName =
        `${therapistProfile?.firstName || ""} ${therapistProfile?.lastName || ""}`.trim() ||
        "לא ידוע";

      await createTicket({
        clinicId: clinicProfile?.id,
        therapistId: therapistProfile?.uid,
        therapistName,
        clinicName: clinicProfile?.name,
        subject: `שאלת הדרכה: ${helpData.title}`,
        description: questionText.trim(),
        category: "help_question",
      }).unwrap();

      setTicketSent(true);
      showSuccess("השאלה נשלחה בהצלחה! נחזור אליך בהקדם.");

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("[CK-HELP] Error sending help question:", error);
      showError("שגיאה בשליחת השאלה. נסה שוב מאוחר יותר.");
    }
  }, [
    questionText,
    therapistProfile,
    clinicProfile,
    helpData.title,
    createTicket,
    showSuccess,
    showError,
    onClose,
  ]);

  const isRichContent = typeof helpData.content !== "string";
  const contentParagraphs = isRichContent
    ? []
    : helpData.content.split("\n").filter((line) => line.trim() !== "");

  const popup = (
    <div className="help-popup-overlay" onClick={handleOverlayClick}>
      <div className="help-popup-modal">
        {/* Header */}
        <div className="help-popup-header">
          <div className="help-popup-icon">
            <Question size={28} weight="duotone" />
          </div>
          <h3 className="help-popup-title">{helpData.title}</h3>
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Content */}
        <div className="help-popup-content">
          {isRichContent
            ? helpData.content
            : contentParagraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}

          {/* Optional image */}
          {helpData.image && (
            <div className="help-popup-image">
              <img src={helpData.image} alt={helpData.title} />
            </div>
          )}

          {/* FAQ Accordion */}
          {faqItems.length > 0 && (
            <div className="help-faq">
              <h4 className="help-faq-heading">שאלות נפוצות</h4>
              <div className="help-faq-list">
                {faqItems.map((item, index) => {
                  const isOpen = openFaqIndex === index;
                  return (
                    <div
                      key={index}
                      className={`help-faq-item ${isOpen ? "open" : ""}`}
                    >
                      <button
                        className="help-faq-question"
                        onClick={() => toggleFaq(index)}
                        type="button"
                        aria-expanded={isOpen}
                      >
                        <span className="help-faq-question-text">{item.q}</span>
                        <CaretDown
                          size={14}
                          weight="bold"
                          className="help-faq-caret"
                        />
                      </button>
                      <div className="help-faq-answer-wrap">
                        <div className="help-faq-answer">
                          <p>{item.a}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Ask Question Form */}
        {showAskForm && !ticketSent && (
          <div className="help-popup-ask-form">
            <textarea
              className="help-popup-textarea"
              placeholder="כתוב את השאלה שלך כאן..."
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              rows={3}
              autoFocus
            />
            <button
              className="help-popup-send-btn"
              onClick={handleSendQuestion}
              disabled={isSending || !questionText.trim()}
              type="button"
            >
              {isSending ? (
                <>
                  <LoaderLoop size="xs" />
                  <span>שולח...</span>
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={16} weight="fill" />
                  <span>שלח שאלה</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Success State */}
        {ticketSent && (
          <div className="help-popup-success">
            <CheckCircle size={24} weight="fill" />
            <span>השאלה נשלחה! נחזור אליך בהקדם.</span>
          </div>
        )}

        {/* Actions */}
        {!ticketSent && (
          <div className="help-popup-actions">
            <button
              className="help-popup-btn help-popup-btn-primary"
              onClick={onClose}
              type="button"
            >
              הבנתי
            </button>
            {!showAskForm && (
              <button
                className="help-popup-btn help-popup-btn-secondary"
                onClick={() => setShowAskForm(true)}
                type="button"
              >
                <ChatText size={16} weight="regular" />
                שאל שאלה
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(popup, document.body);
}

export default HelpPopup;
